import * as React from "react";
import { Redirect } from "react-router-dom";
import queryString from "query-string-for-all";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { I18n as I18nInterface } from "@lingui/core";
import { I18n } from "@lingui/react";
import { t } from "@lingui/macro";
import { Helmet } from "react-helmet";
import { SubNav } from "../../../components/Analysis/SubNav";
import { Monitoring } from "../../../components/Analysis/Monitoring";
import { loadMonitoring } from "../../../redux/modules/monitoring/actionCreators";
import { updateMonitoringUrlHistory } from "../../../redux/modules/urlHistory/actionCreators";
import {
  updateDaysRange,
  updateIsMultiSiteRoute,
} from "../../../redux/modules/_BETA/selections";
import { Wrap } from "../../../components/Layout/Page";
import Loading from "../../../components/Loading";
import { Category } from "../../../components/Analysis/utils/types";
import { getCategoryFromQueryString } from "../../../components/Analysis/utils";

interface ConnectedProps {
  user: Insite.API.User;
  appReady: boolean;
  monitoring: Insite.AppState["monitoring"];
  selectedSites: Insite.AppState["betaSelections"]["selectedSites"];
  shouldRequestMonitoring: Insite.AppState["requestSchedule"]["shouldRequestMonitoring"];
  daysRange: Insite.AppState["betaSelections"]["daysRange"];
  showSnoozed: Insite.AppState["insights"]["showSnoozed"];
}

interface DispatchProps {
  requestMonitoring: typeof loadMonitoring;
  updateHistory: typeof updateMonitoringUrlHistory;
  updateTimeRange: typeof updateDaysRange;
  setMultiSite: typeof updateIsMultiSiteRoute;
}

type Props = RouteComponentProps & ConnectedProps & DispatchProps;

const mapStateToProps = (state: any): ConnectedProps => ({
  user: state.auth.user,
  appReady: state.appSetup.appSetupComplete,
  monitoring: state.monitoring,
  selectedSites: state.betaSelections.selectedSites,
  shouldRequestMonitoring: state.requestSchedule.shouldRequestMonitoring,
  daysRange: state.betaSelections.daysRange,
  showSnoozed: state.insights.showSnoozed,
});

const mapDispatchToProps: DispatchProps = {
  requestMonitoring: loadMonitoring,
  updateHistory: updateMonitoringUrlHistory,
  updateTimeRange: updateDaysRange,
  setMultiSite: updateIsMultiSiteRoute,
};

export function getMonitoringTitle(i18n: I18nInterface, category: Category) {
  const trans = {
    connectivity: i18n._(t("monitoring.connectivity")`Connectivity`),
    metering: i18n._(t("monitoring.metering")`Metering`),
    performance: i18n._(t("monitoring.performance")`Performance`),
    comfort: i18n._(t("monitoring.comfort")`Comfort`),
    all: i18n._(t("monitoring.all")`All`),
    null: i18n._(t("monitoring.other")`Other`),
  };

  return trans[category];
}

function onChangeCategory(
  history: RouteComponentProps["history"],
  updateHistory: typeof updateMonitoringUrlHistory,
  setCategory: React.Dispatch<React.SetStateAction<Category>>
) {
  return (category: Category) => {
    setCategory(category);
    const qs = queryString.parse(history.location.search);
    const stringified = queryString.stringify({ ...qs, category });
    history.replace({ search: stringified });
    updateHistory(history.location, history.action);
  };
}

const MonitoringScene: React.FunctionComponent<Props> = ({
  history,
  appReady,
  user,
  monitoring,
  requestMonitoring,
  shouldRequestMonitoring,
  updateHistory,
  updateTimeRange,
  daysRange,
  setMultiSite,
  selectedSites,
  showSnoozed,
}) => {
  if (!appReady) {
    return null;
  }
  if (!user.groups.includes("ConserveIt")) return <Redirect to="/actions" />;

  if (monitoring.loading)
    return (
      <Wrap>
        <Loading />
      </Wrap>
    );

  const defaultCategory = getCategoryFromQueryString(history.location.search);
  const [category, setCategory] = React.useState<Category>(defaultCategory);

  React.useEffect(() => {
    if (selectedSites.length > 1) {
      setMultiSite(true);
    }
  }, [selectedSites]);

  React.useEffect(() => {
    if (shouldRequestMonitoring) {
      requestMonitoring();
    }
  }, [shouldRequestMonitoring]);

  return (
    <I18n>
      {({ i18n }) => (
        <React.Fragment>
          <Helmet title={getMonitoringTitle(i18n, category)} />
          <SubNav
            category={category}
            onChangeCategory={onChangeCategory(
              history,
              updateHistory,
              setCategory
            )}
          />
          <Wrap>
            <Monitoring
              history={history}
              updateHistory={updateHistory}
              updateTimeRange={updateTimeRange}
              timeRange={daysRange}
              category={category}
              data={monitoring}
              showSnoozed={showSnoozed}
            />
          </Wrap>
        </React.Fragment>
      )}
    </I18n>
  );
};

export const ConnectedMonitoring = connect(
  mapStateToProps,
  mapDispatchToProps
)(MonitoringScene);
